<template>
  <div class="w-full max-w-md lg:max-w-4xl">
    <div
      class="bg-white/90 backdrop-blur rounded-2xl border border-gray-200 shadow-xl overflow-hidden lg:grid lg:grid-cols-2"
    >
      <!-- Header (left column on lg) -->
      <div
        class="px-8 pt-8 pb-6 text-center border-b border-gray-100 lg:border-b-0 lg:border-r lg:flex lg:flex-col lg:justify-center lg:py-10"
      >
        <span
          class="inline-flex items-center gap-1.5 self-center text-[11px] font-semibold uppercase tracking-wide text-lime-600 bg-outtalentLime/50 border border-outtalentLime rounded-full px-3 py-1 mb-4"
        >
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M9 8c-1.667.667-5.4 2.7-7 5.5m9.5-2.5C9.167 12.333 4 16.4 2 22m10.5-7.5c-1.167 1.167-3.8 4.1-5 6.5"/><path fill="currentColor" d="m14.674 6.45l.673-3.285l2.225 2.51l3.027-.294l-1.768 3.062l1.743 2.639l-3.286-.673l-2.51 2.225l.19-3.156l-3.062-1.768z"/></g></svg>
          Graduate offer
        </span>

        <h1 class="text-2xl font-semibold text-gray-900 leading-tight">
          {{ product?.name }}
        </h1>
        

        <div class="mt-4 flex items-baseline justify-center gap-1.5">
          <span class="text-4xl font-semibold text-gray-900 tracking-tight">
            {{ product?.formatted_amount || `$${product?.amount}` }}
          </span>
          <span class="text-sm text-gray-500">one-time</span>
        </div>
        <p v-if="product?.description" class="text-sm text-gray-500 mt-2 leading-relaxed">
          {{ product?.description }}
        </p>
        <!-- <p class="text-sm text-gray-400 mt-1">
          <template v-if="hasActiveSubscription">
            (Activates when your current subscription period ends)
          </template>
          <template v-else>Access starts immediately after payment.</template>
        </p> -->

        <!-- CTA (left column, wide screens only) -->
        <div class="hidden lg:block mt-8">
          <div
            v-if="purchase"
            class="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3.5 text-left"
          >
            <CheckCircle2 class="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p class="text-sm font-medium text-emerald-800">Package purchased</p>
              <p class="text-xs text-emerald-700 mt-0.5">{{ purchaseWindowLabel }}</p>
            </div>
          </div>
          <template v-else>
            <button
              @click="$emit('pay')"
              :disabled="isLoading"
              class="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-900 text-white text-sm rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
            >
              <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
              {{ isLoading ? 'Redirecting to checkout…' : 'Purchase Package' }}
            </button>
            <p class="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-3">
              <Lock class="w-3 h-3" />
              Secure payment via Stripe
            </p>
          </template>
        </div>
      </div>

      <!-- Right column on lg: features + footer -->
      <div class="lg:flex lg:flex-col text-center">
        <!-- What's included -->
        <div class="px-8 py-6 border-b border-gray-100 lg:flex-1">
          <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
            What's included
          </p>
          <ul class="space-y-2.5 text-left">
            <li
              v-for="feature in features"
              :key="feature"
              class="flex items-start gap-3 text-sm text-gray-700"
            >
              <CheckCircle2 class="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              {{ feature }}
            </li>
          </ul>

          <!-- Free month incentive -->
          <div class="mt-4 flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5">
            <Gift class="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p class="text-xs text-amber-800 leading-relaxed">
              Stay active in your job search during the package and qualify for an
              <span class="font-medium">additional month of support, free</span>.
            </p>
          </div>
        </div>

        <!-- CTA (moves into the left column on lg; only fine print stays here) -->
        <div class="px-8 py-8 lg:py-6">
          <div class="lg:hidden">
            <div
              v-if="purchase"
              class="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3.5 text-left"
            >
              <CheckCircle2 class="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p class="text-sm font-medium text-emerald-800">Package purchased</p>
                <p class="text-xs text-emerald-700 mt-0.5">{{ purchaseWindowLabel }}</p>
              </div>
            </div>
            <template v-else>
              <button
                @click="$emit('pay')"
                :disabled="isLoading"
                class="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
              >
                <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
                {{ isLoading ? 'Redirecting to checkout…' : 'Purchase Package' }}
              </button>
              <p class="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-3">
                <Lock class="w-3 h-3" />
                Secure payment via Stripe
              </p>
            </template>
          </div>
          <p class="text-[11px] text-gray-400 text-center mt-2 lg:mt-0 leading-relaxed">
            <template v-if="hasActiveSubscription">
              One-time, non-refundable payment. Your package period starts after
              your current subscription month ends, replacing your monthly subscription.
            </template>
            <template v-else>
              One-time, non-refundable payment.
            </template>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckCircle2, Gift, Loader2, Lock } from 'lucide-vue-next'
import type { Product, ProductPurchase } from '@/types/subscription'

// Fallback when the product has no features configured in Django admin.
// Source: Job Application Support Package contract amendment (Section 3).
const DEFAULT_FEATURES = [
  'Outtalent Slack community',
  'Live InterviewWatch & Offers databases',
  'Unlimited peer-to-peer mock interviews',
  'Weekly batch meetings with big tech mentors',
  'Monthly 1:1 strategy & feedback session with a mentor',
  'CV recommendations based on your application performance',
  'Referrals to top tech companies',
  'Salary negotiation support'
]

interface Props {
  product: Product | null
  /** Unexpired paid purchase of this product; shows the card in its "purchased" state. */
  purchase?: ProductPurchase | null
  isLoading?: boolean
  hasActiveSubscription?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  purchase: null,
  isLoading: false,
  hasActiveSubscription: false,
})

const features = computed(() =>
  props.product?.features?.length ? props.product.features : DEFAULT_FEATURES
)

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const purchaseWindowLabel = computed(() => {
  const purchase = props.purchase
  if (!purchase) return ''
  const expires = formatDate(purchase.expires_at)
  if (purchase.is_active_access) {
    return expires ? `Access is active until ${expires}.` : 'Access is active.'
  }
  const starts = formatDate(purchase.starts_at)
  if (starts && expires) return `Access starts ${starts} and runs until ${expires}.`
  return 'Access starts when your current subscription month ends.'
})

defineEmits<{
  pay: []
}>()
</script>
